/* eslint-env browser */
import { useEffect, useRef } from 'react';

interface PageMeta {
  title: string;
  description: string;
  canonical?: string;
  /** If true, adds <meta name="robots" content="noindex"> */
  noindex?: boolean;
}

/**
 * Sets document.title and manages <meta name="description"> and
 * <link rel="canonical"> tags for the current page.
 *
 * Falls back gracefully — on unmount, restores the previous title
 * and removes any tags this hook created.
 */
function usePageMeta({ title, description, canonical, noindex }: PageMeta) {
  const prevTitle = useRef(document.title);

  useEffect(() => {
    const savedTitle = prevTitle.current;

    // Title
    document.title = title;

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const createdMeta = !metaDesc;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const createdLink = !link && !!canonical;
    if (canonical) {
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Robots noindex
    let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const createdRobots = !robotsMeta && !!noindex;
    if (noindex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', 'noindex, nofollow');
    }

    // OG tags — update if they exist (set in index.html), don't create
    const ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null;
    if (ogTitle) ogTitle.setAttribute('content', title);

    const ogDesc = document.querySelector(
      'meta[property="og:description"]'
    ) as HTMLMetaElement | null;
    if (ogDesc) ogDesc.setAttribute('content', description);

    const ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
    if (ogUrl && canonical) ogUrl.setAttribute('content', canonical);

    return () => {
      document.title = savedTitle;
      if (createdMeta && metaDesc) metaDesc.remove();
      if (createdLink && link) link.remove();
      if (createdRobots && robotsMeta) robotsMeta.remove();
    };
  }, [title, description, canonical, noindex]);
}

export default usePageMeta;
